import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Lightning, CheckCircle } from "@phosphor-icons/react";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token") || "";
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing invite token"); setLoading(false); return; }
    api.get(`/auth/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((err) => setError(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/accept-invite", { token, password });
      setDone(true);
      if (refresh) await refresh();
      setTimeout(() => navigate("/dashboard"), 1200);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] font-body flex items-center justify-center p-8">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-12">
          <Lightning weight="fill" size={22} className="text-[#FF2A2A]" />
          <span className="font-heading font-black text-xl tracking-tight">RECRUITFLOW</span>
        </div>

        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
          Accept invite
        </div>

        {loading ? (
          <div className="text-sm text-zinc-500 uppercase tracking-[0.2em]">Loading</div>
        ) : done ? (
          <div className="border border-emerald-500 bg-emerald-50 text-emerald-900 text-sm px-4 py-4 rounded-sm flex items-start gap-3" data-testid="accept-done">
            <CheckCircle size={18} weight="fill" className="flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium mb-1">Welcome to RecruitFlow</div>
              <div className="text-xs">Taking you to your deal board…</div>
            </div>
          </div>
        ) : !invite ? (
          <div className="space-y-4">
            <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] text-sm px-4 py-3 rounded-sm">
              {error || "This invite link is invalid or has expired."}
            </div>
            <Link to="/login" className="text-sm text-zinc-950 underline underline-offset-4">Back to sign in</Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl mb-3 leading-[1.05]">
              Welcome,<br />{invite.first_name || invite.name?.split(" ")[0] || "there"}.
            </h1>
            <p className="text-sm text-zinc-500 mb-8">
              {invite.invited_by_name || "Your admin"} invited <b className="text-zinc-950">{invite.email}</b> as a{invite.role === "admin" ? "n admin" : " user"}. Pick a password to get started.
            </p>

            <form onSubmit={submit} className="space-y-5" data-testid="accept-invite-form">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Set password</label>
                <input
                  data-testid="invite-password-input"
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-zinc-200 px-4 py-3 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                  placeholder="At least 8 characters"
                  autoFocus
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Confirm password</label>
                <input
                  data-testid="invite-confirm-input"
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full bg-white border border-zinc-200 px-4 py-3 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                  placeholder="Retype the same password"
                  autoComplete="new-password"
                />
              </div>

              {error && (
                <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] text-sm px-4 py-3 rounded-sm" data-testid="accept-error">{error}</div>
              )}

              <button
                data-testid="accept-invite-btn"
                type="submit"
                disabled={submitting}
                className="w-full bg-zinc-950 text-white py-3.5 rounded-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-50"
              >
                {submitting ? "Setting up..." : "Create my account"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
